import './guest/capture-fragment';
import './instrument';
import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { queryClient } from '@/lib/query_client';
import { initKeycloak } from '@/lib/keycloak';
import { AuthProvider } from '@/context/AuthContext';
import { setupDefaultRegistry } from '@/form-engine/builder/setup-registry';
import { setFlowConfigProvider } from '@/form-engine/query/useFlowConfig';
import { setPageConfigProvider } from '@/form-engine/query/usePageConfig';
import { graphqlClient } from '@/lib/graphql_client';
import { getConfigKey } from '@/lib/config_key';
import { GET_CONFIG_QUERY, type GetConfigResult } from '@/gql/config_query';
import type { FlowConfig, PageConfig } from '@/form-engine/config/types';
import { TooltipProvider } from '@/components/ui/Tooltip';
import { ErrorBoundary, ErrorFallback } from '@/components/shared/ErrorBoundary';
import { Toaster } from '@/components/ui/Toast';
import { runOAuthCallbackBridge } from '@/pages/Integrations/oauth-callback-standalone';
import { runSignupApp } from '@/pages/Signup/signup-standalone';
import { runVerifyApp } from '@/pages/Verify/verify-standalone';
import { runInviteAcceptApp } from '@/pages/InviteAccept/invite-accept-standalone';
import { runSigningReturnApp } from '@/pages/SigningReturn/signing-return-standalone';
import { runDocumentSignatureReturnApp } from '@/pages/DocumentSignatureReturn/document-signature-return-standalone';
import { runGuestApp } from '@/guest/guest-standalone';
import { resolvePublicBootstrapRoute } from './bootstrap-dispatch';
import {
  clearStaleChunkRecoveryMarker,
  STALE_CHUNK_RELOAD_GUARD_MS,
} from '@/lib/chunk-recovery';
import './index.css';

setupDefaultRegistry();

/* ── Config Providers (GraphQL-backed) ── */

setFlowConfigProvider(async (module, key, country) => {
  const configKey = getConfigKey({ module, key, country });
  const result = await graphqlClient.request<GetConfigResult>(GET_CONFIG_QUERY, { configKey });
  if (!result.getConfig) throw new Error(`Config not found: ${configKey}`);
  return result.getConfig.data as unknown as FlowConfig;
});

setPageConfigProvider(async (module, key, country) => {
  const configKey = getConfigKey({ module, key, country });
  const result = await graphqlClient.request<GetConfigResult>(GET_CONFIG_QUERY, { configKey });
  if (!result.getConfig) throw new Error(`Config not found: ${configKey}`);
  return result.getConfig.data as unknown as PageConfig;
});

/*
 * `defaultErrorComponent` catches a render throw inside a route and renders the fallback in the
 * Outlet — so the sidebar, navbar and the rest of the shell survive, and the user can navigate
 * away instead of reloading. `reset` is TanStack Router's own retry, which re-renders the route.
 *
 * The <ErrorBoundary> wrapper below is the outer net for throws OUTSIDE the router (the auth,
 * query and tooltip providers). Before this, neither existed: any render throw unmounted the
 * whole tree and left a blank page.
 */
const router = createRouter({
  routeTree,
  defaultErrorComponent: ({ error, reset }) => <ErrorFallback error={error} onRetry={reset} />,
});

// Register TanStack Router with Sentry for route-aware tracing
Sentry.addIntegration(
  Sentry.tanstackRouterBrowserTracingIntegration(router),
);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

async function bootstrap() {
  // Every public, pre-auth path MUST run before — and without — Keycloak (ADR-001). The matching
  // logic itself lives in `./bootstrap-dispatch` as a pure function (T07) so it can be unit-tested
  // directly, without mounting a React root or hitting the network — see that file for why each
  // path is here, and `bootstrap-dispatch.test.ts` / `main.bootstrap.test.ts` for the runtime
  // proof that this dispatch actually skips `initKeycloak()` and the GraphQL client for the paths
  // that must never reach either.
  const publicRoute = resolvePublicBootstrapRoute(window.location.pathname);
  switch (publicRoute?.kind) {
    case 'oauth-callback':
      runOAuthCallbackBridge();
      return;
    case 'signup':
      runSignupApp();
      return;
    case 'verify':
      runVerifyApp();
      return;
    case 'invite-accept':
      runInviteAcceptApp();
      return;
    case 'signing-return':
      runSigningReturnApp(publicRoute.outcome);
      return;
    case 'document-signature-return':
      runDocumentSignatureReturnApp(publicRoute.outcome);
      return;
    case 'guest':
      runGuestApp();
      return;
  }

  await initKeycloak();

  createRoot(document.getElementById('root')!, {
    // React 19 error hooks — report caught/uncaught errors to Sentry
    onCaughtError: Sentry.reactErrorHandler(),
    onUncaughtError: Sentry.reactErrorHandler(),
  }).render(
    <StrictMode>
      <ErrorBoundary label="root">
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <RouterProvider router={router} />
            </TooltipProvider>
            {/* Note (QA FE-5): @tanstack/react-query-devtools mounts its Solid panel
                via createRoot/createPortal into a ref'd container that React 19.2
                flags ("Cannot use a ref … as a container … if it also sets children
                text"). It's a dev-only, prod-gated console warning (no user impact);
                resolves when TanStack ships a React-19-clean devtools build. */}
            {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />}
            <Toaster />
          </QueryClientProvider>
        </AuthProvider>
      </ErrorBoundary>
    </StrictMode>,
  );

  // A stale lazy chunk triggers one guarded reload in ErrorFallback. Clear that marker only after
  // the refreshed app has stayed alive long enough to prove it did not hit the same missing asset.
  window.setTimeout(clearStaleChunkRecoveryMarker, STALE_CHUNK_RELOAD_GUARD_MS);
}

bootstrap().catch(console.error);
